import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Event, EventFile, EventRsvp } from '@/lib/types/database';
import {
  useEventRsvps,
  useUpsertRsvp,
  useDeleteRsvp,
} from '@/lib/hooks/useRsvps';
import { useEventNotificationSubscription } from '@/lib/hooks/useNotificationPreferences';
import {
  formatRsvpDeadline,
  isRsvpDeadlinePassed,
  isEventAtCapacity,
} from '@/lib/utils/events.utils';
import { EventDetailsContent } from '@/content/EventDetailsContent';
import { ActionDrawer } from '@/components/ui/action-drawer';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { EventLink } from '@/components/events/EventLink';
import { EventAlertBanner } from '@/components/events/EventAlertBanner';
import { EventHeader } from '@/components/events/EventHeader';
import { EventAdminRsvpCard } from '@/components/events/EventAdminRsvpCard';
import { EventAudienceMetadataCard } from '@/components/events/EventAudienceMetadataCard';
import { EventRsvpCard } from '@/components/events/EventRsvpCard';
import { EventInfoSection } from '@/components/events/EventInfoSection';
import { EventReminderSelect } from '@/components/events/EventReminderSelect';
import { EnableNotificationsCard } from '@/components/home/EnableNotificationsCard';
import { usePushNotificationStatus } from '@/lib/hooks/usePushNotificationStatus';
import { useEventsBySeries } from '@/lib/hooks/useEvents';
import { EventSeriesDrawer } from '@/components/events/EventSeriesDrawer';
import { EventMaterialsList } from '@/components/events/EventMaterialsList';
import { useEventFileOpenUrl, useEventFiles } from '@/lib/hooks/useEventFiles';
import { useEventAudienceMetadata } from '@/lib/hooks/useEventAudienceMetadata';

interface EventDetailsProps {
  event: Event;
  rsvp: EventRsvp | null;
  userId: string | null;
  isAdmin?: boolean;
}

export function EventDetails({
  event,
  rsvp,
  userId,
  isAdmin: _isAdmin = false,
}: EventDetailsProps) {
  const navigate = useNavigate();
  const { data: allRsvps = [], isLoading: isRsvpsLoading } = useEventRsvps(
    event.id
  );
  const upsertRsvp = useUpsertRsvp();
  const deleteRsvp = useDeleteRsvp();
  const { toast, showToast, hideToast } = useToastNotification();
  const { data: eventFiles = [], isLoading: isEventFilesLoading } =
    useEventFiles(event.id);
  const openEventFile = useEventFileOpenUrl();
  const {
    data: audienceMetadata,
    isLoading: isAudienceMetadataLoading,
    error: audienceMetadataError,
  } = useEventAudienceMetadata(event.id, _isAdmin);

  // Fetch series events if this event is part of a series
  const { data: seriesEvents = [] } = useEventsBySeries(event.series_id);

  // Calculate current event position in series
  const seriesInfo = useMemo(() => {
    if (!event.series_id || seriesEvents.length === 0) {
      return null;
    }
    const currentIndex = seriesEvents.findIndex((e) => e.id === event.id);
    return {
      currentPosition: currentIndex >= 0 ? currentIndex + 1 : 1,
      totalEvents: seriesEvents.length,
    };
  }, [event.series_id, event.id, seriesEvents]);

  // Event notification subscription hook
  const {
    subscription,
    selectedReminders,
    toggleReminder: originalToggleReminder,
    isUpdating: isReminderUpdating,
  } = useEventNotificationSubscription(event.id);

  // Check push notification status
  const pushStatus = usePushNotificationStatus();

  // Check if user has any notifications enabled for this event
  const hasNotificationsEnabled =
    subscription &&
    (subscription.remind_3_days ||
      subscription.remind_1_day ||
      subscription.remind_1_hr ||
      subscription.notify_changes);

  // Local state for UI
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [seriesDrawerOpen, setSeriesDrawerOpen] = useState(false);
  const [pendingRsvpStatus, setPendingRsvpStatus] = useState<
    'yes' | 'no' | null
  >(null);

  const goingCount = allRsvps.filter((rsvp) => rsvp.status === 'yes').length;

  const deadlinePassed = event.rsvp_deadline
    ? isRsvpDeadlinePassed(event.rsvp_deadline)
    : false;
  const atCapacity = isEventAtCapacity(event, allRsvps);
  // An attended attendee already holds a seat, so they count as going for
  // capacity purposes — a full event must not hide their own RSVP state.
  const isGoing = rsvp?.status === 'yes' || rsvp?.status === 'attended';
  // RSVP footer shows if: not at capacity, OR at capacity but user is already going
  const canRsvp = !deadlinePassed && userId && (!atCapacity || isGoing);

  // Handle RSVP confirmation from drawer
  const handleConfirmRsvp = async () => {
    if (!userId || !pendingRsvpStatus) return;

    try {
      // Always upsert to set the status - this handles both new RSVPs and status changes
      const result = await upsertRsvp.mutateAsync({
        event_id: event.id,
        user_id: userId,
        status: pendingRsvpStatus,
      });

      // The RSVP is saved at this point, so the confirmation is done with.
      setDrawerOpen(false);
      setPendingRsvpStatus(null);

      const isSeriesEvent = !!result.seriesId;
      const statusText =
        pendingRsvpStatus === 'yes' ? 'attending' : 'not attending';
      const savedDescription = isSeriesEvent
        ? `You're ${statusText} all events in this series.`
        : `You're ${statusText} this event.`;

      // A reminder outage is a partial success: the RSVP stands, only the
      // automatic 1-day reminder is missing.
      if (result.reminderSyncFailed) {
        showToast({
          title: 'RSVP confirmed',
          description: `${savedDescription} We couldn't update your reminder settings.`,
          variant: 'success',
        });
        return;
      }

      showToast({
        title: 'RSVP confirmed',
        description: savedDescription,
        variant: 'success',
      });
    } catch (error) {
      // The RSVP itself failed. Leave the drawer open so the user can retry
      // in place rather than re-navigating to the event.
      showToast({
        title: 'RSVP failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to update your RSVP. Please try again.',
        variant: 'error',
      });
    }
  };

  // Handle button clicks - open drawer for non-selected button
  const handleRsvpButtonClick = useCallback(
    (status: 'yes' | 'no') => {
      const currentStatus = rsvp?.status;

      // If clicking the already selected button, do nothing
      if (
        (status === 'yes' && currentStatus === 'yes') ||
        (status === 'no' && currentStatus === 'no')
      ) {
        return;
      }

      // Open drawer for confirmation
      setPendingRsvpStatus(status);
      setDrawerOpen(true);
    },
    [rsvp?.status]
  );

  // Wrapper for toggleReminder with toast notifications
  const handleToggleReminder = useCallback(
    async (option: Parameters<typeof originalToggleReminder>[0]) => {
      try {
        await originalToggleReminder(option);
        // Show success toast
        showToast({
          title: 'Reminder updated',
          description: 'Your reminder preferences have been saved.',
          variant: 'success',
        });
      } catch (error) {
        // Show error toast
        showToast({
          title: 'Update failed',
          description:
            error instanceof Error
              ? error.message
              : 'Failed to update reminder preferences. Please try again.',
          variant: 'error',
        });
      }
    },
    [originalToggleReminder, showToast]
  );

  const handleOpenFile = useCallback(
    async (file: EventFile) => {
      try {
        const url = await openEventFile.mutateAsync(file.id);
        window.location.assign(url);
      } catch (error) {
        showToast({
          title: 'Could not open PDF',
          description:
            error instanceof Error
              ? error.message
              : 'Please try opening this material again.',
          variant: 'error',
        });
      }
    },
    [openEventFile, showToast]
  );

  // Determine drawer content based on pending status
  const drawerContent =
    pendingRsvpStatus === 'yes'
      ? EventDetailsContent.confirmRsvp
      : EventDetailsContent.cancelRsvp;

  return (
    <>
      <ActionDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) {
            setPendingRsvpStatus(null);
          }
        }}
        title={drawerContent.title}
        description={drawerContent.message}
        confirmLabel={drawerContent.confirmButton}
        cancelLabel={drawerContent.cancelButton}
        confirmVariant={pendingRsvpStatus === 'yes' ? 'default' : 'destructive'}
        onConfirm={handleConfirmRsvp}
        isLoading={upsertRsvp.isPending || deleteRsvp.isPending}
      />

      <div className="w-full max-w-4xl mx-auto bg-slate-600">
        <div className="px-4 pb-2">
          {/* Alert Banners */}
          {deadlinePassed && <EventAlertBanner type="deadline" />}

          {atCapacity && !deadlinePassed && !isGoing && (
            <EventAlertBanner type="capacity" />
          )}

          {/* Event Header Section */}
          <EventHeader
            event={event}
            isAdmin={_isAdmin}
            onEditClick={() =>
              navigate({
                to: '/events/$eventId/edit',
                params: { eventId: event.id.toString() },
              })
            }
            hasNotificationsEnabled={!!hasNotificationsEnabled}
            seriesBanner={
              seriesInfo
                ? {
                    currentPosition: seriesInfo.currentPosition,
                    totalEvents: seriesInfo.totalEvents,
                    onBannerClick: () => setSeriesDrawerOpen(true),
                  }
                : undefined
            }
          />

          {_isAdmin && (
            <>
              <EventAdminRsvpCard
                eventId={event.id}
                goingCount={goingCount}
                isLoading={isRsvpsLoading}
                onViewList={() =>
                  navigate({
                    to: '/events/$eventId/attendance',
                    params: { eventId: event.id.toString() },
                    search: { tab: 'going' },
                  })
                }
              />
              <EventAudienceMetadataCard
                metadata={audienceMetadata}
                isLoading={isAudienceMetadataLoading}
                error={audienceMetadataError}
              />
            </>
          )}

          {/* RSVP Card */}
          {canRsvp && (
            <EventRsvpCard
              currentStatus={
                rsvp?.status === 'attended' ? 'yes' : (rsvp?.status ?? null)
              }
              deadlinePassed={deadlinePassed}
              atCapacity={atCapacity}
              deadlineText={
                event.rsvp_deadline
                  ? formatRsvpDeadline(event.rsvp_deadline)
                  : undefined
              }
              isLoading={upsertRsvp.isPending || deleteRsvp.isPending}
              isAttended={rsvp?.status === 'attended'}
              onGoingClick={() => handleRsvpButtonClick('yes')}
              onNotGoingClick={() => handleRsvpButtonClick('no')}
              isSeriesEvent={!!event.series_id}
            />
          )}

          {/* Event Info Section */}
          <EventInfoSection event={event} />

          <EventMaterialsList
            files={eventFiles}
            isLoading={isEventFilesLoading}
            isOpeningId={openEventFile.variables ?? null}
            onOpenFile={handleOpenFile}
          />

          {/* Event Link Section */}
          {event?.event_link && (
            <div className="py-6">
              <EventLink
                href={event.event_link}
                label={event.link_name ?? undefined}
              />
            </div>
          )}

          {/* Remind Me Section */}
          {pushStatus === 'enabled' ? (
            <EventReminderSelect
              selectedReminders={selectedReminders}
              onToggle={handleToggleReminder}
              isUpdating={isReminderUpdating}
            />
          ) : pushStatus === 'disabled' ? (
            <EnableNotificationsCard showLaterButton={false} />
          ) : null}
        </div>
      </div>

      {/* Series Drawer */}
      {event.series_id && seriesEvents.length > 0 && (
        <EventSeriesDrawer
          open={seriesDrawerOpen}
          onOpenChange={setSeriesDrawerOpen}
          events={seriesEvents}
          currentEventId={event.id}
          onEventClick={(eventId) => {
            navigate({
              to: '/events/$eventId',
              params: { eventId: eventId.toString() },
            });
          }}
        />
      )}

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </>
  );
}
